/**
 * Plain yes/no confirmation sheet — same shell as RoleSelectModal (bottom sheet on
 * mobile, centred dialog on desktop) but for questions that have exactly two answers.
 *
 * Use this instead of RoleSelectModal when the caller already knows which role it
 * needs: asking "are you a jobseeker or an employer?" when the only useful answer is
 * "jobseeker" makes the user re-decide something the screen has already decided.
 *
 * The confirm button copies the header's "Ажлын зар нийтлэх" treatment — dark navy,
 * cyan on hover — so the primary action looks the same everywhere in the app.
 */

import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import useEscapeKey from "../hooks/useEscapeKey";
import useDialogFocus from "../hooks/useDialogFocus";

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  confirmLabel,
  cancelLabel,
}) {
  const { t } = useTranslation();

  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);
  const panelRef = useDialogFocus(isOpen);

  const confirm = () => {
    onConfirm?.();
    onClose?.();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="confirm-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[120] flex items-end md:items-center justify-center p-0 md:p-4"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            tabIndex={-1}
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98, transition: { duration: 0.18 } }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="relative w-full md:w-[460px] max-h-[90vh] bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col focus:outline-none"
          >
            {/* Above the scroller, so a long title cannot push the close button out of reach. */}
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close", "Хаах")}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
            >
              <FiX size={18} className="text-slate-600" />
            </button>

            {/* The sheet is capped at 90vh, so its body has to be the scroller. The bottom
                padding clears the iOS home indicator (viewport-fit=cover in index.html),
                which otherwise sits over the lower half of the confirm button. */}
            <div className="flex-1 overflow-y-auto px-6 pt-10 pb-[calc(1.75rem+env(safe-area-inset-bottom,0px))] md:px-9 md:pt-11 md:pb-9">
              <h2 id="confirm-modal-title" className="text-[22px] md:text-[26px] font-bold text-center text-[#020618] leading-snug px-2">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-2.5 text-center text-sm md:text-base text-slate-400">{subtitle}</p>
              )}

              <div className="mt-8 flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-12 rounded-[99px] border border-slate-200 text-[15px] font-bold text-[#020618] hover:border-[#020618] transition-colors"
                >
                  {cancelLabel || t("common.no", "Үгүй")}
                </button>
                <button
                  type="button"
                  onClick={confirm}
                  className="flex-1 h-12 rounded-[99px] bg-[#020618] text-[15px] font-bold text-white hover:bg-[#29EAFF] hover:text-[#0F172B] transition-colors"
                >
                  {confirmLabel || t("common.yes", "Тийм")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
